import {Input, InputNumber, Select, Switch, Tooltip} from 'antd';
import {
  FEE_TYPES,
  feeTypeHasLevel,
  feeTypeHasPerExchange,
  feeTypeLabel,
  isFeeAmountDisabled,
  pruneFeeForType,
} from './feeRules';
import styles from './feeScheduleTable.module.scss';

/**
 * A fee schedule as a compact table.
 *
 * Controlled on a plain array, so it works both inside an antd `Form.Item`
 * (which injects value/onChange into its direct child — do not wrap this in a
 * div there) and against ordinary state, which is what the per-instrument-type
 * overrides use.
 *
 * The stacked-card `FeeSchedule` component is the other editor in the app. It
 * carries the extra machinery entry/exit/recurring fees need on an instrument
 * — proration, frequency, exit vectors — and is the right one where those
 * apply. This one is for the flat exchange-level schedule, where five labelled
 * fields per fee made four fees three screens tall. Both read the same rules
 * from `feeRules`.
 */

const FEE_LEVELS = [
  {value: 'ASSET', label: 'Asset class'},
  {value: 'INSTRUMENTS', label: 'All instruments in it'},
  {value: 'INSTRUMENT', label: 'This instrument'},
];

export const FeeScheduleTable = ({
  value,
  onChange,
  disabled,
  addLabel = '＋ Add fee',
}) => {
  const rows = value ?? [];

  const write = next => onChange?.(next);

  const patch = (index, changes) =>
    write(rows.map((fee, i) => (i === index ? {...fee, ...changes} : fee)));

  // Changing the type drops the amounts it does not charge on, so a row can
  // never carry a number its type ignores.
  const setType = (index, feeType) =>
    write(
      rows.map((fee, i) =>
        i === index ? pruneFeeForType({...fee, feeType}) : fee,
      ),
    );

  return (
    <div className={styles.feeList}>
      {rows.length > 0 && (
        <div className={styles.feeHead}>
          <span>Name</span>
          <span>Type</span>
          <span>Fixed</span>
          <span>Variable (%)</span>
          <span>Level</span>
          <Tooltip title="Charge the entry fee once per exchange instead of once overall. It narrows whatever the level already matches, so a user who paid entry on an NGX stock still pays it on their first BRVM one.">
            <span>Per exch.</span>
          </Tooltip>
          <span />
        </div>
      )}

      {rows.map((fee, index) => {
        const fixedOff = isFeeAmountDisabled(fee, 'fixed');
        const variableOff = isFeeAmountDisabled(fee, 'variable');
        return (
          // eslint-disable-next-line react/no-array-index-key
          <div className={styles.feeRow} key={index}>
            <Input
              size="small"
              placeholder="Standard"
              disabled={disabled}
              value={fee?.feeName}
              onChange={event => patch(index, {feeName: event.target.value})}
            />
            <Select
              size="small"
              placeholder="Type"
              disabled={disabled}
              value={fee?.feeType}
              onChange={feeType => setType(index, feeType)}
              options={FEE_TYPES.map(type => ({
                value: type,
                label: feeTypeLabel(type),
              }))}
            />
            <InputNumber
              size="small"
              min={0}
              className={styles.fullWidth}
              disabled={disabled || fixedOff}
              // A disabled box showing 0 reads as "this fee is zero" rather
              // than "this type does not charge one".
              value={fixedOff ? null : fee?.fixedFeeAmount}
              placeholder={fixedOff ? '—' : '0'}
              onChange={amount => patch(index, {fixedFeeAmount: amount})}
            />
            <InputNumber
              size="small"
              min={0}
              max={100}
              className={styles.fullWidth}
              disabled={disabled || variableOff}
              value={variableOff ? null : fee?.variableFeeAmount}
              placeholder={variableOff ? '—' : '0'}
              onChange={amount => patch(index, {variableFeeAmount: amount})}
            />
            {feeTypeHasLevel(fee?.feeType) ? (
              <Select
                size="small"
                allowClear
                placeholder="Optional"
                disabled={disabled}
                value={fee?.level ?? undefined}
                onChange={level => patch(index, {level: level ?? null})}
                options={FEE_LEVELS}
              />
            ) : (
              // Held open so the columns stay aligned down the table.
              <span className={styles.feeNotApplicable}>—</span>
            )}
            {feeTypeHasPerExchange(fee?.feeType) ? (
              <Switch
                size="small"
                disabled={disabled}
                checked={Boolean(fee?.perExchange)}
                onChange={perExchange => patch(index, {perExchange})}
              />
            ) : (
              <span className={styles.feeNotApplicable}>—</span>
            )}
            <button
              type="button"
              className={styles.feeRemove}
              aria-label={`Remove ${fee?.feeName || 'fee'}`}
              disabled={disabled}
              onClick={() => write(rows.filter((_, i) => i !== index))}>
              ×
            </button>
          </div>
        );
      })}

      <button
        type="button"
        className={styles.addFee}
        disabled={disabled}
        onClick={() => write([...rows, {}])}>
        {addLabel}
      </button>
    </div>
  );
};

export default FeeScheduleTable;
